import axios from 'axios';
import { attachAuthInterceptors } from '../authTokenRefresh';
import { config } from '../../config';
import { ApiServices, ApiResponse, Pool } from '../../types/api';
import { poolMonitoringApi } from '../poolMonitoringApi';
import { 
  RegisterData, 
  UpdateUserData, 
  ChangePasswordData, 
  ResetPasswordData 
} from '../../types/auth';
import { handleApiResponse, extractArrayFromApiPayload } from '../../utils/api';
import type { Administrator, AdministratorCreate, AdministratorUpdate } from '../../types/auth';

// API 클라이언트 생성
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachAuthInterceptors(apiClient);

// API 서비스 정의
const authApi = {
  // 기본 인증 메서드
  login: async (credentials: { username: string; password: string }) => {
    const response = await apiClient.post('/api/auth/login', credentials);
    return response.data;
  },
  register: async (data: RegisterData) => {
    const response = await apiClient.post('/api/auth/register', data);
    return response.data;
  },
  logout: async () => {
    const response = await apiClient.post('/api/auth/logout');
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  },
  updateUser: async (userId: number, data: UpdateUserData) => {
    const response = await apiClient.put(`/api/auth/users/${userId}`, data);
    return response.data;
  },
  changePassword: async (data: ChangePasswordData) => {
    const response = await apiClient.post('/api/auth/change-password', data);
    return response.data;
  },
  resetPassword: async (data: ResetPasswordData) => {
    const response = await apiClient.post('/api/auth/reset-password', data);
    return response.data;
  },
  
  // 관리자 인증 관련 메서드
  verifyAdminPassword: async (password: string) => {
    const response = await apiClient.post('/api/auth/admin/password/verify', { password });
    return response.data;
  },
  verifyControlPassword: async (password: string) => {
    const response = await apiClient.post('/api/auth/verify-control-password', { password });
    return response.data;
  },
  getAdminPassword: async () => {
    const response = await apiClient.get('/api/auth/admin-password');
    return response.data;
  },
  changeAdminPassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await apiClient.post('/api/auth/change-admin-password', data);
    return response.data;
  },
  getAdminPasswordHistory: async () => {
    const response = await apiClient.get('/api/auth/admin-password-history');
    return response.data;
  }
};

const poolApi = {
  getPools: async (params?: { search?: string; sort?: string; order?: string; page?: number; page_size?: number }): Promise<ApiResponse<Pool[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.sort) queryParams.append('sort', params.sort);
      if (params?.order) queryParams.append('order', params.order);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.page_size) queryParams.append('page_size', String(params.page_size));

      const url = `/api/pools/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      return handleApiResponse<Pool[]>(response);
    } catch (error: unknown) {
      console.error('[poolApi] 시설 목록 조회 오류:', error);
      const err = error as { response?: { status?: number; data?: { message?: string; detail?: string } }; message?: string };
      const detail = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
      return {
        success: false,
        data: null,
        message: detail || '시설 목록을 불러오기 실패',
        status: err.response?.status ?? 500,
      };
    }
  },
  getPoolById: async (id: number) => {
    const response = await apiClient.get(`/api/pools/${id}`);
    return response.data;
  },
  getPoolByCode: async (code: string): Promise<ApiResponse<Pool>> => {
    try {
      console.log(`[poolApi] 시설 코드(${code})로 조회 시작`);
      const response = await apiClient.get(`/api/pools/code/${code}`);
      console.log('[poolApi] 시설 코드 조회 응답:', response.data);
      
      if (response.status === 200 && response.data) {
        return {
          success: true,
          data: response.data,
          message: '시설 정보 조회 성공'
        };
      }
      throw new Error('시설 정보 조회 실패');
    } catch (error) {
      console.error('[poolApi] 시설 코드 조회 오류:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.detail || error.message || '시설 정보 조회 실패'
      };
    }
  },
  getPool: async (id: number) => {
    const response = await apiClient.get(`/api/pools/${id}`);
    return response.data;
  },
  getPoolSettings: async (poolId: number | string) => {
    const response = await apiClient.get(`/api/pools/${poolId}/settings`);
    return response.data;
  },
  updatePoolSettings: async (poolId: number | string, data: any) => {
    const response = await apiClient.put(`/api/pools/${poolId}/settings`, data);
    return response.data;
  },
  createPool: async (data: any) => {
    // 시설 생성 API 요청
    console.log(`[api] 시설 생성 API 요청: /api/pools`, data);
    const response = await apiClient.post('/api/pools', data);
    return response.data;
  },
  updatePool: async (id: number, data: any) => {
    const response = await apiClient.put(`/api/pools/${id}`, data);
    return response.data;
  },
  deletePool: async (id: number) => {
    const response = await apiClient.delete(`/api/pools/${id}`);
    return response.data;
  },
  deletePoolByCode: async (code: string) => {
    const response = await apiClient.delete(`/api/pools/${code}`);
    return response.data;
  },
  getPoolMonitoringData: async (poolCode: string) => {
    const response = await apiClient.get(`/api/pools/code/${poolCode}/monitoring`);
    return response.data;
  }
};

// CCTV 관련 API
const cctvApi = {
  getAll: async (poolId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/cctvs`);
    return response.data;
  },
  getById: async (poolId: number, cctvId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/cctvs/${cctvId}`);
    return response.data;
  },
  create: async (poolId: number, data: any) => {
    const response = await apiClient.post(`/api/pools/${poolId}/cctvs`, data);
    return response.data;
  },
  update: async (poolId: number, cctvId: number, data: any) => {
    const response = await apiClient.put(`/api/pools/${poolId}/cctvs/${cctvId}`, data);
    return response.data;
  },
  delete: async (poolId: number, cctvId: number) => {
    const response = await apiClient.delete(`/api/pools/${poolId}/cctvs/${cctvId}`);
    return response.data;
  }
};

// 웨어러블 관련 API
const wearableApi = {
  getAll: async (poolId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/wearables`);
    return response.data;
  },
  getById: async (poolId: number, wearableId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/wearables/${wearableId}`);
    return response.data;
  },
  getData: async (poolId: number, wearableId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/wearables/${wearableId}/data`);
    return response.data;
  },
  create: async (poolId: number, data: any) => {
    const response = await apiClient.post(`/api/pools/${poolId}/wearables`, data);
    return response.data;
  },
  update: async (poolId: number, wearableId: number, data: any) => {
    const response = await apiClient.put(`/api/pools/${poolId}/wearables/${wearableId}`, data);
    return response.data;
  },
  delete: async (poolId: number, wearableId: number) => {
    const response = await apiClient.delete(`/api/pools/${poolId}/wearables/${wearableId}`);
    return response.data;
  }
};

const monitoringApi = {
  getMonitoringData: async (poolCode: string) => {
    const response = await apiClient.get(`/api/monitoring/${poolCode}`);
    return response.data;
  },
  getMonitoringDataById: async (id: number) => {
    const response = await apiClient.get(`/api/monitoring/${id}`);
    return response.data;
  },
  getPoolStatus: async (poolId: number) => {
    const response = await apiClient.get(`/api/monitoring/pools/${poolId}/status`);
    return response.data;
  },
  getAlerts: async (poolId: number) => {
    const response = await apiClient.get(`/api/monitoring/pools/${poolId}/alerts`);
    return response.data;
  },
  createMonitoringData: async (data: any) => {
    const response = await apiClient.post('/api/monitoring', data);
    return response.data;
  },
  updateMonitoringData: async (id: number, data: any) => {
    const response = await apiClient.put(`/api/monitoring/${id}`, data);
    return response.data;
  },
  deleteMonitoringData: async (id: number) => {
    const response = await apiClient.delete(`/api/monitoring/${id}`);
    return response.data;
  }
};

const sensorApi = {
  getSensors: async (poolCode: string) => {
    const response = await apiClient.get(`/api/sensors?pool_code=${poolCode}`);
    return response.data;
  },
  getSensorById: async (id: number) => {
    const response = await apiClient.get(`/api/sensors/${id}`);
    return response.data;
  },
  getSensorData: async (poolId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/sensors`);
    return response.data;
  },
  getSensorHistory: async (poolId: number, sensorId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/sensors/${sensorId}/history`);
    return response.data;
  },
  createSensor: async (data: any) => {
    const response = await apiClient.post('/api/sensors', data);
    return response.data;
  },
  updateSensor: async (id: number, data: any) => {
    const response = await apiClient.put(`/api/sensors/${id}`, data);
    return response.data;
  },
  deleteSensor: async (id: number) => {
    const response = await apiClient.delete(`/api/sensors/${id}`);
    return response.data;
  }
};

const contactApi = {
  getContacts: async () => {
    const response = await apiClient.get('/api/contacts');
    return response.data;
  },
  getContactById: async (id: number) => {
    const response = await apiClient.get(`/api/contacts/${id}`);
    return response.data;
  },
  updateContact: async (contactId: number, data: any) => {
    const response = await apiClient.put(`/api/v1/contacts/${contactId}`, data);
    return response.data;
  },
  createContact: async (data: any) => {
    const response = await apiClient.post('/api/contacts', data);
    return response.data;
  },
  deleteContact: async (id: number) => {
    const response = await apiClient.delete(`/api/contacts/${id}`);
    return response.data;
  }
};

// 시설 비밀번호 API
const poolPasswordApi = {
  getPoolPasswords: async (poolId: number) => {
    const response = await apiClient.get(`/api/pools/${poolId}/passwords`);
    return response.data;
  },
  getPoolPasswordById: async (id: number) => {
    const response = await apiClient.get(`/api/pool-passwords/${id}`);
    return response.data;
  },
  updatePoolPassword: async (poolId: number, passwordId: number, data: any) => {
    const response = await apiClient.put(`/api/v1/pools/${poolId}/passwords/${passwordId}`, data);
    return response.data;
  },
  updatePoolPasswords: async (poolId: number, password: string) => {
    // 백엔드: POST /api/pools/{id}/password — bcrypt 저장 (PUT .../passwords 아님)
    const response = await apiClient.post(`/api/pools/${poolId}/password`, { password });
    const data = response.data as { success?: boolean; message?: string };
    return {
      success: Boolean(data?.success ?? response.status < 300),
      message: data?.message || '비밀번호가 변경 완료',
      data,
    };
  },
  createPoolPassword: async (data: any) => {
    const response = await apiClient.post('/api/pool-passwords', data);
    return response.data;
  },
  deletePoolPassword: async (id: number) => {
    const response = await apiClient.delete(`/api/pool-passwords/${id}`);
    return response.data;
  }
};

// 관리자 API (/api/administrators 미등록 — /api/users·/api/auth/me 사용)
function mapUserRecordToAdministrator(u: Record<string, unknown>): Administrator {
  const id = u.id != null ? String(u.id) : '';
  const roleRaw = String(u.role ?? 'admin');
  const role: 'admin' | 'manager' = roleRaw === 'manager' ? 'manager' : 'admin';
  return {
    id,
    username: String(u.username ?? ''),
    name: String(u.full_name ?? u.name ?? u.username ?? ''),
    role,
    isMainAdmin: u.is_main_admin === true || u.isMainAdmin === true,
    email: u.email != null ? String(u.email) : undefined,
    phone: u.phone != null ? String(u.phone) : undefined,
    lastLogin: u.last_login != null ? String(u.last_login) : undefined,
    created_at: String(u.created_at ?? ''),
    updated_at: String(u.updated_at ?? ''),
  };
}

function isAdminRoleUser(u: Record<string, unknown>): boolean {
  const role = String(u.role ?? '');
  return role === 'admin' || role === 'manager' || u.is_superuser === true;
}

function usersToAdministratorList(raw: unknown): Administrator[] {
  const rows = extractArrayFromApiPayload(raw) as Record<string, unknown>[];
  const filtered = rows.filter(isAdminRoleUser);
  const source = filtered.length > 0 ? filtered : rows;
  return source.map(mapUserRecordToAdministrator);
}

const administratorApi = {
  getAdministrators: async (): Promise<ApiResponse<Administrator[]>> => {
    try {
      const response = await apiClient.get('/api/users/');
      return {
        success: true,
        data: usersToAdministratorList(response.data),
        status: response.status,
        message: '관리자 목록 조회 성공',
      };
    } catch (error: unknown) {
      console.error('[administratorApi] 목록 조회 오류:', error);
      const err = error as { response?: { status?: number; data?: { detail?: string; message?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message:
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          '관리자 목록을 불러오기 실패',
        status: err.response?.status ?? 500,
      };
    }
  },
  getCurrentAdministrator: async (): Promise<ApiResponse<Administrator>> => {
    try {
      const response = await apiClient.get('/api/auth/me');
      const u = response.data as Record<string, unknown>;
      if (!u?.id) {
        return { success: false, data: null, message: '로그인 정보를 확인 불가', status: 401 };
      }
      return {
        success: true,
        data: mapUserRecordToAdministrator(u),
        status: response.status,
        message: '요청 성공',
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '관리자 정보 조회 실패',
        status: 500,
      };
    }
  },
  getAdministratorById: async (id: number): Promise<ApiResponse<Administrator>> => {
    try {
      const response = await apiClient.get(`/api/users/${id}`);
      return {
        success: true,
        data: mapUserRecordToAdministrator(response.data as Record<string, unknown>),
        status: response.status,
        message: '요청 성공',
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '관리자 조회 실패',
        status: 500,
      };
    }
  },
  updateAdministrator: async (adminId: number, data: AdministratorUpdate): Promise<ApiResponse<Administrator>> => {
    try {
      const body: Record<string, unknown> = { ...data };
      if (body.name != null && body.full_name == null) {
        body.full_name = body.name;
      }
      if (body.isMainAdmin != null && body.is_main_admin == null) {
        body.is_main_admin = body.isMainAdmin;
      }
      const response = await apiClient.put(`/api/users/${adminId}`, body);
      return {
        success: true,
        data: mapUserRecordToAdministrator(response.data as Record<string, unknown>),
        status: response.status,
        message: '관리자 정보가 수정됨',
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '관리자 수정 실패',
        status: 500,
      };
    }
  },
  createAdministrator: async (data: AdministratorCreate): Promise<ApiResponse<Administrator>> => {
    try {
      const email =
        data.email?.trim() ||
        `${String(data.username).trim() || 'admin'}@local.pool.invalid`;
      const response = await apiClient.post('/api/users/', {
        username: data.username,
        password: data.password,
        email,
        full_name: data.name,
        role: data.role || 'admin',
        is_main_admin: data.isMainAdmin ?? false,
        is_active: true,
        is_superuser: data.role !== 'manager',
      });
      return {
        success: true,
        data: mapUserRecordToAdministrator(response.data as Record<string, unknown>),
        status: response.status,
        message: '관리자가 생성됨',
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '관리자 생성 실패',
        status: 500,
      };
    }
  },
  deleteAdministrator: async (id: number): Promise<ApiResponse<null>> => {
    try {
      await apiClient.delete(`/api/users/${id}`);
      return { success: true, data: null, status: 204, message: '삭제 완료' };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '관리자 삭제 실패',
        status: 500,
      };
    }
  },
  changeAdministratorPassword: async (
    id: number,
    data: { currentPassword?: string; newPassword: string }
  ): Promise<ApiResponse<null>> => {
    try {
      await apiClient.put(`/api/users/${id}`, { password: data.newPassword });
      return { success: true, data: null, status: 200, message: '비밀번호가 변경 완료' };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      return {
        success: false,
        data: null,
        message: err.response?.data?.detail || err.message || '비밀번호 변경 실패',
        status: 500,
      };
    }
  },
};

const apiServices = {
  authApi,
  poolApi,
  cctvApi,
  wearableApi,
  monitoringApi,
  poolMonitoringApi,
  sensorApi,
  contactApi,
  poolPasswordApi,
  administratorApi
};

export default apiServices; 